/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

$aspectRatioHeight: 100 * (1 - customDivide(9, 16));
$padding-constant: 168px;
$image-layer-width: 360px;

:host {
  display: block;
  height: 100%;
  width: 100%;
  font-family: $font-family-base;

  * {
    box-sizing: border-box;
    outline: 0 none;
    text-rendering: optimizelegibility;
  }
}

:host([promo-flag-sm]) {
  margin-top: 18px !important;

  .#{$prefix}content-layer-wrapper > *:first-child,
  .#{$prefix}image-layer-content-image {
    margin-top: 18px;
  }

  .#{$prefix}content-layer {
    margin-bottom: 18px;
  }
}

:host([promo-flag-lg]) {
  margin-top: 26px !important;

  .#{$prefix}content-layer-wrapper > *:first-child,
  .#{$prefix}image-layer-content-image {
    margin-top: 26px;
  }

  .#{$prefix}content-layer {
    margin-bottom: 26px;
  }
}

.#{$prefix}card-teaser-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: block;
  text-decoration: none;

  .#{$prefix}card-teaser-wrapper-inner {
    position: relative;
    height: 100%;
    transition: border $transition-duration ease-in-out;
    background: v(backgroundColor);

    &.#{$prefix}gradient {
      background: v(gradientColor);
    }

    .#{$prefix}content-layer {
      position: relative;
      height: 100%;
      display: flex;
      flex-direction: row;

      .#{$prefix}content-layer-spacer {
        position: relative;
        width: calc($image-layer-width / 2);
        flex-shrink: 0;
      }

      .#{$prefix}content-layer-wrapper {
        position: relative;
        width: 100%;
        height: auto;
        min-height: $image-layer-width;
        padding: 48px 48px 48px calc(48px + $padding-constant);
        word-wrap: break-word; // For EDGE 18
        word-break: break-word; // For Chrome, Safari, Firefox
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        transition: border $transition-duration ease-in-out;

        @include for-size(desktop) {
          padding: 40px 40px 40px calc(40px + $padding-constant);
        }

        @include for-size(tablet) {
          padding: 32px 32px 32px calc(32px + $padding-constant);
        }

        @include for-size(phone) {
          padding: 24px 24px 24px calc(24px + $padding-constant);
        }

        .#{$prefix}card-teaser-category {
          color: v(categoryColor);
          display: block;
          font-size: 14px;
          line-height: 18px;
          margin-bottom: 16px;
        }

        .#{$prefix}card-teaser-headline {
          display: block;

          eon-ui-headline {
            color: v(headlineColor);
          }

          &.#{$prefix}alternative-1 {
            eon-ui-headline {
              color: v(alternativeHeadlineColor1);
            }
          }

          &.#{$prefix}alternative-2 {
            eon-ui-headline {
              color: v(alternativeHeadlineColor2);
            }
          }
        }

        .#{$prefix}card-teaser-content {
          display: flex;
          flex-direction: column;

          > slot {
            display: flex;
            flex-direction: column;
            gap: 24px;

            @include for-size("phone") {
              gap: 16px;
            }
          }
        }

        &.#{$prefix}text-left {
          text-align: left;

          .#{$prefix}card-teaser-content {
            align-items: flex-start;

            &.#{$prefix}has-content {
              ::slotted(*:not([slot])) {
                justify-content: flex-start;
              }
            }
          }
        }

        &.#{$prefix}text-center {
          text-align: center;

          .#{$prefix}card-teaser-links {
            align-items: center;
          }

          .#{$prefix}card-teaser-content {
            align-items: center;

            &.#{$prefix}has-content {
              ::slotted(*:not([slot])) {
                justify-content: center;
              }
            }
          }
        }

        &.#{$prefix}text-right {
          text-align: right;

          .#{$prefix}card-teaser-links {
            align-items: flex-end;
          }

          .#{$prefix}card-teaser-content {
            align-items: flex-end;

            &.#{$prefix}has-content {
              ::slotted(*:not([slot])) {
                justify-content: flex-end;
              }
            }
          }
        }

        .#{$prefix}card-teaser-links {
          justify-content: flex-end;
          flex-direction: column;
          display: flex;
        }
      }
    }

    .#{$prefix}image-layer {
      position: absolute;
      left: 0;
      top: 0;
      width: $image-layer-width;
      height: 100%;
      z-index: 20;

      .#{$prefix}image-layer-content-image {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: auto;

        &::after {
          @include aspect-ratio(1, 1);
        }

        .#{$prefix}card-teaser-violator {
          z-index: 10;
          position: absolute;
          left: 16px;
          top: 16px;

          &.#{$prefix}position-x-right {
            left: auto;
            right: 16px;
          }

          &.#{$prefix}position-x-center {
            left: 50%;
            transform: translateX(-50%);
          }

          &.#{$prefix}position-y-center {
            top: 50%;
            transform: translateY(-50%);
          }

          &.#{$prefix}position-y-bottom {
            top: auto;
            bottom: 16px;
          }
        }
      }

      .#{$prefix}image-layer-background-image {
        position: absolute;
        left: 0;
        top: 0;
        width: calc($image-layer-width / 2);
        height: 100%;

        &.#{$prefix}colored-background {
          background-color: v(backgroundColor);
        }

        .#{$prefix}image-layer-spacer {
          display: none;
        }
      }
    }
  }

  .#{$prefix}promo-flag {
    position: absolute;
    top: 0;
    transform: translateY(-50%);
    max-width: calc(100% - 32px);
    z-index: 30;

    @include for-size(tablet) {
      max-width: calc(100% - 24px);
    }

    @include for-size(phone) {
      max-width: calc(100% - 16px);
    }

    &.#{$prefix}left {
      left: 0;
    }

    &.#{$prefix}right {
      right: 0;
    }

    &.#{$prefix}center {
      left: 0;
      width: 100%;
      max-width: none;
    }
  }
}

::slotted(*:not([scheme])) {
  color: v(contentColor) !important;
}

::slotted(*:not([slot])) {
  display: inline-flex;
  @include for-size(phone) {
    width: 100%;
  }
}

// As default content gap is 24px and 16px for phone, we have added theses margins to reach the desired headline margins
// (even if headline is placed inside of content slot).
::slotted(eon-ui-headline) {
  margin-bottom: 16px;

  @include for-size(desktop) {
    margin-bottom: 8px;
  }

  @include for-size(tablet) {
    margin-bottom: 0px;
  }

  @include for-size(phone) {
    margin-bottom: 8px;
  }
}

::slotted([slot="cta"]) {
  display: inline-flex;
}

::slotted(eon-ui-link) {
  --contentColor: var(--linkColor) !important;
  --contentHoverColor: var(--linkHoverColor) !important;
  --contentFocusColor: var(--linkHoverColor) !important;
}

::slotted(eon-ui-linkList) {
  --linklistColor: var(--linkColor);
  --linklistHoverColor: var(--linkHoverColor);
}

:host(.container-breakpoint-800),
:host(.container-breakpoint-400) {
  &:host([promo-flag-sm]) {
    margin-top: 18px !important;

    .#{$prefix}content-layer {
      margin-bottom: 0;
    }

    .#{$prefix}image-layer-content-image,
    .#{$prefix}content-layer-wrapper {
      margin-top: 18px;
    }
  }

  &:host([promo-flag-lg]) {
    margin-top: 26px !important;

    .#{$prefix}content-layer {
      margin-bottom: 0;
    }

    .#{$prefix}image-layer-content-image,
    .#{$prefix}content-layer-wrapper {
      margin-top: 26px;
    }
  }

  .#{$prefix}card-teaser-wrapper {
    .#{$prefix}card-teaser-wrapper-inner {
      display: flex;
      flex-direction: column;

      .#{$prefix}content-layer {
        width: 100%;

        .#{$prefix}content-layer-spacer {
          display: none;
        }

        .#{$prefix}content-layer-wrapper {
          min-height: unset;
          padding: calc(24px + ($aspectRatioHeight * 1%)) 24px 24px;
          margin-top: -#{$aspectRatioHeight} + "%";

          @include for-size(tablet) {
            padding: calc(32px + ($aspectRatioHeight * 1%)) 32px 32px;
          }

          @include for-size(desktop) {
            padding: calc(40px + ($aspectRatioHeight * 1%)) 40px 40px;
          }

          @include for-size(desktop-l-or-larger) {
            padding: calc(48px + ($aspectRatioHeight * 1%)) 48px 48px;
          }
        }

        .#{$prefix}card-teaser-links {
          flex: 1;
        }
      }

      .#{$prefix}image-layer {
        position: relative;
        width: 100%;
        height: auto;
        display: flex;
        flex-direction: column;

        &::after {
          @include aspect-ratio(1, 1);
        }

        .#{$prefix}image-layer-background-image {
          width: 100%;
          height: auto;

          &::after {
            @include aspect-ratio(16, 9);
          }
        }

        .#{$prefix}image-layer-spacer {
          display: block;
          background-color: v(backgroundColor);
        }
      }

      .#{$prefix}image-layer-fixed-width {
        max-height: $image-layer-width;

        .#{$prefix}image-layer-background-image {
          height: calc($image-layer-width / 2);
        }

        .#{$prefix}image-layer-content-image {
            max-width: $image-layer-width;
            left: 50%;
            transform: translateX(-50%);
        }
      }
    }
  }
}
@include teaserHeadlinesMargin(".#{$prefix}card-teaser-headline");
@include teaserCtaMargin(".#{$prefix}card-teaser-links");
